import { useForm } from "react-hook-form";

import type { CreateFastMenuActionItemRequest } from "@/api/types";
import { Form } from "@/components/Form/Form";
import { FormHiddenInput } from "@/components/Form/FormHiddenInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormRadioCard, FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Stack } from "@/design-system/Stack";
import { createRequiredStringRule } from "@/helpers/rules";
import { isValidHttpUrl } from "@/helpers/validation";
import { useTranslation } from "@/translations";

const FORM_ID = "custom-url-modal-form";

type CustomUrlModalProps = ModalBaseProps & {
  isLoading: boolean;
  onSubmit: ({
    id,
    label,
    url,
    useType,
  }: {
    id: string;
    label: string;
    url: string;
    useType: CreateFastMenuActionItemRequest["useType"];
  }) => Promise<void>;
  defaultValues?: FormValues;
};

interface FormValues {
  id: string;
  url: string;
  label: string;
  useType: CreateFastMenuActionItemRequest["useType"];
}

export function CustomUrlModal({
  isOpened,
  isLoading,
  onOpenChange,
  onSubmit,
  defaultValues,
}: CustomUrlModalProps): React.ReactNode {
  const { t } = useTranslation();
  const form = useForm<FormValues>({ defaultValues });

  async function handleSubmit(formValues: FormValues) {
    await onSubmit({
      id: formValues.id,
      label: formValues.label.trim(),
      url: formValues.url?.trim(),
      useType: formValues.useType,
    });

    form.reset();
    onOpenChange(false);
  }

  return (
    <Modal.Root
      title={t("page.plus-button-links.custom-url.modal.translations.title")}
      description={t("page.plus-button-links.custom-url.modal.translations.description")}
      size="sm"
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        <Form.Root id={FORM_ID} formMethods={form} onSubmit={handleSubmit}>
          <FormHiddenInput<FormValues> name="id" />
          <Stack gap="md">
            <Stack gap="md">
              <FormField label={t("page.plus-button-links.custom-url.modal.translations.input-name")} required>
                <FormInput<FormValues>
                  name="label"
                  placeholder={t("page.plus-button-links.custom-url.modal.translations.input-placeholder")}
                  rules={{
                    validate: {
                      required: createRequiredStringRule(
                        t,
                        "page.plus-button-links.custom-url.modal.translations.input-name",
                      ),
                    },
                  }}
                />
              </FormField>
            </Stack>
            <Stack gap="sm">
              <h4 className="text-body-emphasis">{t("page.plus-button-links.custom-url.modal.url.title")}</h4>
              <FormField label={t("page.plus-button-links.custom-url.modal.url.input-name")} required>
                <FormInput<FormValues, "url">
                  name="url"
                  placeholder={t("page.plus-button-links.custom-url.modal.url.input-placeholder")}
                  rules={{
                    validate: {
                      required: createRequiredStringRule(t, "page.plus-button-links.custom-url.modal.url.input-name"),
                      urlFormat: (value) => {
                        const isValid = isValidHttpUrl(value.trim());

                        if (!isValid) {
                          return t("page.plus-button-links.maintainer.modal.url.input-error");
                        }
                      },
                    },
                  }}
                />
              </FormField>
            </Stack>
            <Stack gap="sm" data-testid="url-options-container">
              <h4 className="text-body-emphasis">{t("page.plus-button-links.custom-url.modal.usage-type.title")}</h4>
              <FormRadioCardGroup layout="vertical">
                <FormRadioCard<FormValues, "useType">
                  name="useType"
                  value="webPage"
                  label={t("page.plus-button-links.custom-url.modal.usage-type.radio.web-page.title")}
                  description={t("page.plus-button-links.custom-url.modal.usage-type.radio.web-page.description")}
                />
                <FormRadioCard<FormValues, "useType">
                  name="useType"
                  value="ticketFlow"
                  label={t("page.plus-button-links.custom-url.modal.usage-type.radio.ticket-flow.title")}
                  description={t("page.plus-button-links.custom-url.modal.usage-type.radio.ticket-flow.description")}
                />
              </FormRadioCardGroup>
            </Stack>
          </Stack>
        </Form.Root>
      </Modal.Body>
      <Modal.Actions>
        <Button type="submit" form={FORM_ID} variant="filled" isLoading={isLoading}>
          {t("common.action.save")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
